<script lang="ts">
	import { Responsive } from "responsive"
	import { type Snippet } from "svelte"
	import { ImageModifierController } from "./ImageModifierController.svelte"
	import { MaskBase } from "luxedo-canvas/asset"
	import { LoadingSpinner } from "misc-ui"

	interface Props {
		controller: ImageModifierController
		canvas: Snippet
	}

	let { controller, canvas }: Props = $props()

	let heading = $state("Outline your projection space")
	let description = $state(
		`${Responsive.isMobile ? "Tap" : "Click"} to outline your projection space. ${Responsive.isMobile ? "Tap" : "Click"} "Complete" to autocomplete the outline and move to the next step.`
	)

	let step: "drawing" | "removing" | "approving" = $state("drawing")

	let hasClicked = $state(false)
	let mask: MaskBase
	let isLoading = $state(false)

	async function begin() {
		isLoading = false
		step = "drawing"
		hasClicked = false
		controller.imageEditor.editor.canvas.on("mouse:down", handleCanvasClick)

		heading = "Outline your projection space"
		description = `${Responsive.isMobile ? "Tap" : "Click"} to outline your projection space. ${Responsive.isMobile ? "Tap" : "Click"} "Complete" to autocomplete the outline and move to the next step.`

		mask = await controller.createMask("freedraw", "Facade", () => (isLoading = true))
		isLoading = false

		step = "removing"
		heading = "Remove background"
		description = `${Responsive.isMobile ? "Tap" : "Click"} on the section you want to project onto.`

		await controller.triggerRemoval()

		step = "approving"
		heading = "Is the background hidden?"
		description = `Does this clearly match your projection space with the background removed?`

		controller.imageEditor.editor.canvas.off("mouse:down", handleCanvasClick)
	}

	function handleCanvasClick() {
		hasClicked = true
	}

	function deny() {
		controller.denyRemoval(false)
		controller.deselectMask(mask)

		// removes the grey outline from the mask
		controller.imageEditor.editor.masks.deregister(mask)
		controller.imageEditor.editor.canvas.requestRenderAll()

		begin()
	}

	function approve() {
		isLoading = true
		controller.approveRemoval()
	}

	function completeMask() {
		controller.imageEditor.editor.mode.get().complete()
		controller.imageEditor.editor.canvas.requestRenderAll()
	}

	$effect(() => {
		// Because the editor instance gets swapped out, when switching out components, this has to be wrapped in a setTimeout or it will try to use the last editor instance which gets overwritten
		if (controller)
			setTimeout(() => {
				begin()
			})
	})
</script>

<div class="image-modify">
	<div class="heading">
		<h3>
			{heading}
		</h3>
		<p>
			{description}
		</p>
	</div>
	{@render canvas()}
	<div class="button-container">
		{#if isLoading}
			<LoadingSpinner height="2rem" color="var(--color-main)" />
		{:else if step === "approving"}
			<button class="link-button" onclick={controller.onCancel}>Cancel</button>
			<button onclick={deny} class="warn">No</button>
			<button onclick={approve}>Yes</button>
		{:else if step === "drawing"}
			<button class="link-button" onclick={controller.onCancel}>Cancel</button>
			<button onclick={completeMask} disabled={!hasClicked}>Complete</button>
		{/if}
	</div>
</div>

<style>
	.image-modify {
		height: 100%;
		width: 100%;
		display: flex;
		flex-direction: column;
		overflow: hidden;
		--sidebar-width: 0;
	}

	.heading {
		padding: 1rem;
	}

	.link-button {
		padding-right: 1rem;
	}

	p {
		color: var(--color-text);
		margin: 0;
	}

	.button-container {
		margin-bottom: 0;
		position: absolute;
		bottom: 1rem;
		right: 1rem;
		width: fit-content;
	}

	:global(.isMobile) p {
		text-align: center;
		line-height: 1.4em;
	}
</style>
